import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../service/Api";
import { logout as authLogout } from "./AuthSlice";

// Type for country response from django-countries registry
export interface Country {
  code: string;
  name: string;
}

export interface CountryState {
  countries: Country[];
  loading: boolean;
  error: string | null;
  initialFetchAttempted: boolean;
}

// Initial state - empty until loaded from backend
const initialState: CountryState = {
  countries: [],
  loading: false,
  error: null,
  initialFetchAttempted: false,
};

// Async thunk to fetch countries from backend
type CountryThunkState = {
  country: CountryState;
};

export const fetchCountries = createAsyncThunk<
  Country[],
  void,
  { state: CountryThunkState; rejectValue: string }
>(
  "country/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get<Country[]>(
        "/api/companies/countries/"
      );
      // Return the full country objects with both code and name
      return response.data;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      return rejectWithValue(
        errorMessage || "Failed to fetch countries"
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { country } = getState() as CountryThunkState;

      if (country.loading) {
        return false;
      }

      if (country.countries.length > 0) {
        return false;
      }

      return true;
    },
  }
);

const countrySlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authLogout, (state) => {
        state.countries = [];
        state.loading = false;
        state.error = null;
        state.initialFetchAttempted = false;
      })
      // Fetch countries
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.initialFetchAttempted = true;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
        state.error = null;
        state.initialFetchAttempted = true;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.initialFetchAttempted = true;
        // Keep existing countries on error to avoid empty list
      });
  },
});

export const { clearError } = countrySlice.actions;
export default countrySlice.reducer;